/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListBuiltinExclusionsRequest,
  ListBuiltinExclusionsSecurity,
} from "../models/operations/listbuiltinexclusions.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildBuiltinExclusionsQuery,
  BuiltinExclusionsQueryData,
  prefetchBuiltinExclusions,
  queryKeyBuiltinExclusions,
} from "./builtinExclusions.core.js";
export {
  buildBuiltinExclusionsQuery,
  type BuiltinExclusionsQueryData,
  prefetchBuiltinExclusions,
  queryKeyBuiltinExclusions,
};

export type BuiltinExclusionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listBuiltinExclusions risk
 *
 * @remarks
 * List the built-in exclusion library (known-safe values suppressed before they reach exclusions), grouped by category.
 */
export function useBuiltinExclusions(
  request?: ListBuiltinExclusionsRequest | undefined,
  security?: ListBuiltinExclusionsSecurity | undefined,
  options?: QueryHookOptions<
    BuiltinExclusionsQueryData,
    BuiltinExclusionsQueryError
  >,
): UseQueryResult<BuiltinExclusionsQueryData, BuiltinExclusionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildBuiltinExclusionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listBuiltinExclusions risk
 *
 * @remarks
 * List the built-in exclusion library (known-safe values suppressed before they reach exclusions), grouped by category.
 */
export function useBuiltinExclusionsSuspense(
  request?: ListBuiltinExclusionsRequest | undefined,
  security?: ListBuiltinExclusionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    BuiltinExclusionsQueryData,
    BuiltinExclusionsQueryError
  >,
): UseSuspenseQueryResult<
  BuiltinExclusionsQueryData,
  BuiltinExclusionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildBuiltinExclusionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setBuiltinExclusionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: BuiltinExclusionsQueryData,
): BuiltinExclusionsQueryData | undefined {
  const key = queryKeyBuiltinExclusions(...queryKeyBase);

  return client.setQueryData<BuiltinExclusionsQueryData>(key, data);
}

export function invalidateBuiltinExclusions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "exclusions",
      "listBuiltinExclusions",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllBuiltinExclusions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "exclusions", "listBuiltinExclusions"],
  });
}
